import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { ReactNode, useMemo, useState } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { calcIsolatedMarginAssigned } from '@flipster/features/src/shared/calculator/adjust-margin';
import { Pnl } from '@flipster/features/src/shared/pnl';
import { useClosePosition } from '@flipster/features/src/shared/trade-actions';
import { openPositionsBunja } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getFlipsterConfigsV1Key, setLocalStorage } from '@flipster/utils/localStorage';

interface Props {
  position: OpenPosition | LegacyOpenPosition;
  currency: string;
  qtyPrecision: number;
  isOpen: boolean;
  onClose: () => void;
  onExit?: () => void;
}

const CloseAirdropPositionModal = ({ position, currency, qtyPrecision, isOpen, onClose, onExit }: Props) => {
  const transPositionClose = useTranslations('Position_close.r250401');
  const transPosition = useTranslations('Position.r250401');
  const transAssets = useTranslations('Assets.r250401');
  const transCommon = useTranslations('Common.r250401');
  const { user } = useAuth();
  const { closeFullPosition } = useClosePosition();
  const [isCloseImmediatelyChecked, setIsCloseImmediatelyChecked] = useState(false);
  const { openPositionsAtom } = useBunja(openPositionsBunja);
  const openPositions = useAtomValue(openPositionsAtom);
  const currentPosition = openPositions?.[position.symbol]?.find((p) => p.slot === position.slot) || position;

  /**
   * Airdrop position is isolated mode only, but for safety
   */
  const calculatedMargin = useMemo(() => {
    switch (position.marginType) {
      case 'Cross':
        return position.initPositionMargin;
      case 'Isolated':
        return calcIsolatedMarginAssigned(
          position.isolatedMarginInitial.toString(),
          position.isolatedMarginAdded?.toString(),
          position.isolatedMarginRealizedFee.toString(),
        );
      default:
        // oxlint-disable-next-line no-unused-expressions
        position satisfies never;
        // oxlint-disable-next-line no-console
        console.error(`Unexpected margin type`);
        return '0';
    }
  }, [position]);

  const items = [
    {
      label: transPosition('Estimated_pnl'),
      value: (
        <Pnl number={currentPosition.unrealizedPnl} percent={currentPosition.calcUnrealizedPnlPercent}>
          <Pnl.Number /> <Pnl.Percent />
        </Pnl>
      ),
    },
    {
      label: transAssets('Margin'),
      value: (
        <Formatted.Number
          value={calculatedMargin}
          abs
          zeroString='-'
          precision={UsdtPrecision}
          toFixed
          suffix='USDT Bonus'
        />
      ),
    },
    {
      label: transAssets('Quantity'),
      value: (
        <Formatted.Number value={currentPosition.quantity} abs precision={qtyPrecision} toFixed suffix={currency} />
      ),
    },
    {
      label: transAssets('Est_price'),
      value: (
        <Formatted.Number
          value={currentPosition.side === 'Long' ? currentPosition.bidPrice : currentPosition.askPrice}
          zeroString='-'
          precision={UsdtPrecision}
          toFixed
          suffix={UsdtCurrency}
        />
      ),
    },
    {
      label: transAssets('Entry_price'),
      value: (
        <Formatted.Number
          value={currentPosition.avgEntryPrice}
          zeroString='-'
          precision={UsdtPrecision}
          toFixed
          suffix={UsdtCurrency}
        />
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label='' />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 24 })}>
          <div className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>
            {transPositionClose('Close_your_airdrop_question')}
          </div>
          <div
            className={stack({
              py: 16,
              gap: 4,
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderColor: 'border.default.level-2',
            })}
          >
            {items.map((item) => (
              <Item key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
          <Checkbox
            checked={isCloseImmediatelyChecked}
            onChange={(e) => {
              const { checked } = e.target;
              setIsCloseImmediatelyChecked(checked);
              if (user?.userId) {
                setLocalStorage(getFlipsterConfigsV1Key(user.userId, 'skipAirdropPositionCloseModal'), checked);
              }
            }}
            css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-5' }}
          >
            {transPositionClose('Full_close_without_confirmation_option')}
          </Checkbox>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={transCommon('Cancel')}
          onClick={() => {
            if (isCloseImmediatelyChecked) {
              setIsCloseImmediatelyChecked(false);
              if (user?.userId) {
                setLocalStorage(getFlipsterConfigsV1Key(user.userId, 'skipAirdropPositionCloseModal'), false);
              }
            }
            onClose();
          }}
        />
        <Button
          size='large'
          label={transPositionClose('Close_now')}
          onClick={() => {
            closeFullPosition({ position });
            onClose();
          }}
        />
      </Modal.Footer>
    </Modal>
  );
};

const Item = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className={hstack({ h: 22, justify: 'space-between' })}>
    <span className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-2' })}>{label}</span>
    <span className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' })}>{value}</span>
  </div>
);

export default CloseAirdropPositionModal;
